import { useState, useCallback, useEffect } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  LIST_DESTINATIONS,
  GET_DESTINATION,
  GET_DESTINATION_ANALYTICS,
  CREATE_DESTINATION,
  UPDATE_DESTINATION,
  DELETE_DESTINATION,
  ATTACH_DESTINATION_TO_CONVERSATION,
  DETACH_DESTINATION_FROM_CONVERSATION,
  GET_TENANT_DESTINATION_ANALYTICS,
} from "../services/graphql/destination-operations";
import type {
  DestinationEntity,
  CreateDestinationInput,
  UpdateDestinationInput,
  DestinationFilterInput,
  DestinationConnection,
  DestinationAnalytics,
} from "../types/destination-core-types";

export interface UseDestinationsOptions {
  filter?: DestinationFilterInput;
  limit?: number;
  autoFetch?: boolean;
}

export interface UseDestinationsResult {
  destinations: DestinationEntity[];
  loading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  error: Error | null;
  total: number;
  nextToken?: string;
  hasMore: boolean;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  createDestination: (
    input: CreateDestinationInput
  ) => Promise<DestinationEntity>;
  updateDestination: (
    input: UpdateDestinationInput
  ) => Promise<DestinationEntity>;
  deleteDestination: (id: string) => Promise<boolean>;
  attachToConversation: (
    destinationId: string,
    conversationId: string
  ) => Promise<boolean>;
  detachFromConversation: (
    destinationId: string,
    conversationId: string
  ) => Promise<boolean>;
}

export function useDestinations(
  options: UseDestinationsOptions = {}
): UseDestinationsResult {
  const { filter, limit = 25, autoFetch = true } = options;
  const [destinations, setDestinations] = useState<DestinationEntity[]>([]);
  const [total, setTotal] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);

  // Query for listing destinations
  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery(LIST_DESTINATIONS, {
    variables: { filter, limit },
    skip: !autoFetch,
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (data?.listDestinations) {
        setDestinations(data.listDestinations.items || []);
        setTotal(data.listDestinations.total || 0);
        setNextToken(data.listDestinations.nextToken);
        setHasMore(!!data.listDestinations.nextToken);
      }
    },
  });

  // Lazy query for loading more destinations
  const [loadMoreDestinations, { loading: loadMoreLoading }] = useLazyQuery(
    LIST_DESTINATIONS,
    {
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        if (data?.listDestinations) {
          setDestinations((prev) => [
            ...prev,
            ...(data.listDestinations.items || []),
          ]);
          setNextToken(data.listDestinations.nextToken);
          setHasMore(!!data.listDestinations.nextToken);
        }
      },
    }
  );

  // Mutations
  const [createDestinationMutation, { loading: createLoading }] = useMutation(
    CREATE_DESTINATION,
    {
      onCompleted: (data) => {
        if (data?.createDestination) {
          setDestinations((prev) => [data.createDestination, ...prev]);
          setTotal((prev) => prev + 1);
        }
      },
    }
  );

  const [updateDestinationMutation, { loading: updateLoading }] = useMutation(
    UPDATE_DESTINATION,
    {
      onCompleted: (data) => {
        if (data?.updateDestination) {
          setDestinations((prev) =>
            prev.map((dest) =>
              dest.id === data.updateDestination.id
                ? data.updateDestination
                : dest
            )
          );
        }
      },
      onError: (error) => {
        console.error("Failed to update destination:", error);
      },
    }
  );

  const [deleteDestinationMutation, { loading: deleteLoading }] = useMutation(
    DELETE_DESTINATION,
    {
      // Remove the update function to prevent it from running on failed mutations
      // We'll handle state updates manually in the deleteDestination function
      onCompleted: (data, { variables }) => {
        if (data?.deleteDestination && variables?.id) {
          // Update local state
          setDestinations((prev) =>
            prev.filter((dest) => dest.id !== variables.id)
          );
          setTotal((prev) => prev - 1);
        }
      },
      onError: (error) => {
        console.error("Failed to delete destination:", error);
      },
      // Disable error policy to prevent retries
      errorPolicy: "none",
    }
  );

  const [attachToConversationMutation] = useMutation(
    ATTACH_DESTINATION_TO_CONVERSATION
  );
  const [detachFromConversationMutation] = useMutation(
    DETACH_DESTINATION_FROM_CONVERSATION
  );

  // Callback functions
  const refetch = useCallback(async () => {
    try {
      await refetchList();
    } catch (error) {
      console.error("Failed to refetch destinations:", error);
    }
  }, [refetchList]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadMoreLoading) return;

    try {
      await loadMoreDestinations({
        variables: {
          filter,
          limit,
          nextToken,
        },
      });
    } catch (error) {
      console.error("Failed to load more destinations:", error);
    }
  }, [
    hasMore,
    loadMoreLoading,
    loadMoreDestinations,
    filter,
    limit,
    nextToken,
  ]);

  const createDestination = useCallback(
    async (input: CreateDestinationInput): Promise<DestinationEntity> => {
      if (createLoading) {
        throw new Error("Creation already in progress");
      }

      try {
        const result = await createDestinationMutation({
          variables: { input },
        });

        if (!result.data?.createDestination) {
          throw new Error("Failed to create destination");
        }

        return result.data.createDestination;
      } catch (error) {
        console.error("Failed to create destination:", error);
        throw error;
      }
    },
    [createDestinationMutation, createLoading]
  );

  const updateDestination = useCallback(
    async (input: UpdateDestinationInput): Promise<DestinationEntity> => {
      try {
        const result = await updateDestinationMutation({
          variables: { input },
        });

        if (!result.data?.updateDestination) {
          throw new Error("Failed to update destination");
        }

        return result.data.updateDestination;
      } catch (error) {
        console.error("Failed to update destination:", error);
        throw error;
      }
    },
    [updateDestinationMutation]
  );

  const deleteDestination = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteDestinationMutation({
          variables: { id },
        });

        return !!result.data?.deleteDestination;
      } catch (error: any) {
        // If the error is "not found", it might have been already deleted
        if (
          error.message?.includes("not found") ||
          error.message?.includes("access denied")
        ) {
          console.warn(
            `Destination ${id} may have been already deleted:`,
            error.message
          );
          // Remove from local state if it was already deleted
          setDestinations((prev) => prev.filter((dest) => dest.id !== id));
          setTotal((prev) => Math.max(0, prev - 1));
          return true;
        }

        console.error("Failed to delete destination:", error);
        throw error;
      }
    },
    [deleteDestinationMutation]
  );

  const attachToConversation = useCallback(
    async (destinationId: string, conversationId: string): Promise<boolean> => {
      try {
        const result = await attachToConversationMutation({
          variables: { destinationId, conversationId },
        });

        return !!result.data?.attachDestinationToConversation;
      } catch (error) {
        console.error("Failed to attach destination to conversation:", error);
        throw error;
      }
    },
    [attachToConversationMutation]
  );

  const detachFromConversation = useCallback(
    async (destinationId: string, conversationId: string): Promise<boolean> => {
      try {
        const result = await detachFromConversationMutation({
          variables: { destinationId, conversationId },
        });

        return !!result.data?.detachDestinationFromConversation;
      } catch (error) {
        console.error("Failed to detach destination from conversation:", error);
        throw error;
      }
    },
    [detachFromConversationMutation]
  );

  const loading = listLoading || loadMoreLoading;
  const error = listError;

  return {
    destinations,
    loading,
    createLoading,
    updateLoading,
    deleteLoading,
    error,
    total,
    nextToken,
    hasMore,
    refetch,
    loadMore,
    createDestination,
    updateDestination,
    deleteDestination,
    attachToConversation,
    detachFromConversation,
  };
}

export interface UseDestinationOptions {
  id: string;
  autoFetch?: boolean;
}

export interface UseDestinationResult {
  destination: DestinationEntity | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useDestination(
  options: UseDestinationOptions
): UseDestinationResult {
  const { id, autoFetch = true } = options;

  const { data, loading, error, refetch } = useQuery(GET_DESTINATION, {
    variables: { id },
    skip: !autoFetch || !id,
    fetchPolicy: "cache-and-network",
  });

  const refetchDestination = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refetch destination:", error);
    }
  }, [refetch]);

  return {
    destination: data?.getDestination || null,
    loading,
    error,
    refetch: refetchDestination,
  };
}

export interface UseDestinationAnalyticsOptions {
  destinationId: string;
  dateFrom?: string;
  dateTo?: string;
  autoFetch?: boolean;
}

export interface UseDestinationAnalyticsResult {
  analytics: DestinationAnalytics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useDestinationAnalytics(
  options: UseDestinationAnalyticsOptions
): UseDestinationAnalyticsResult {
  const { destinationId, dateFrom, dateTo, autoFetch = true } = options;

  const { data, loading, error, refetch } = useQuery(
    GET_DESTINATION_ANALYTICS,
    {
      variables: { destinationId, dateFrom, dateTo },
      skip: !autoFetch || !destinationId,
      fetchPolicy: "cache-and-network",
    }
  );

  const refetchAnalytics = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refetch destination analytics:", error);
    }
  }, [refetch]);

  return {
    analytics: data?.getDestinationAnalytics || null,
    loading,
    error,
    refetch: refetchAnalytics,
  };
}

export interface UseTenantDestinationAnalyticsOptions {
  dateFrom?: string;
  dateTo?: string;
  autoFetch?: boolean;
}

export interface TenantDestinationAnalytics {
  tenantId: string;
  totalDestinations: number;
  totalClicks: number;
  uniqueClicks: number;
  averageConversionRate: number;
  deviceBreakdown: {
    PHONE: number;
    DESKTOP: number;
    TABLET: number;
    OTHER: number;
  };
  topDestinations: Array<{
    destinationId: string;
    name: string;
    clicks: number;
    conversionRate: number;
  }>;
}

export interface UseTenantDestinationAnalyticsResult {
  analytics: TenantDestinationAnalytics | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useTenantDestinationAnalytics(
  options: UseTenantDestinationAnalyticsOptions = {}
): UseTenantDestinationAnalyticsResult {
  const { dateFrom, dateTo, autoFetch = true } = options;

  // TODO: Temporarily disabled to fix GraphQL schema issues
  const { data, loading, error, refetch } = useQuery(
    GET_TENANT_DESTINATION_ANALYTICS,
    {
      variables: { dateFrom, dateTo },
      skip: true, // Always skip until schema is fixed
      fetchPolicy: "cache-and-network",
    }
  );

  const refetchAnalytics = useCallback(async () => {
    try {
      await refetch();
    } catch (error) {
      console.error("Failed to refetch tenant destination analytics:", error);
    }
  }, [refetch]);

  return {
    analytics: null, // Temporarily disabled
    loading: false,
    error: null,
    refetch: refetchAnalytics,
  };
}
